import { FC } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { DesignIcon } from '@monorepo/nova-icons';
import { designTokens } from '@monorepo/nova-ui';
import { OTP_CODE_LENGTH } from '../constants/quick-purchase.constants';
import { OtpStatus } from '../hooks/use-quick-purchase-otp';
import {
  StyledButtonRow,
  StyledEmailIconWrapper,
  StyledErrorText,
  StyledFooterText,
  StyledOtpFooter,
  StyledPillButton,
  StyledPillTextField,
  StyledResendLink,
  StyledStepContainer,
  StyledStepTitle,
} from './quick-purchase-modal.styles';

interface OtpFormValues {
  code: string;
}

interface QuickPurchaseOtpStepProps {
  otpForm: UseFormReturn<OtpFormValues>;
  email: string;
  status: OtpStatus;
  isLoading: boolean;
  maxRetriesExceeded: boolean;
  resendCooldownSeconds: number;
  onSubmit: (values: OtpFormValues) => Promise<void>;
  onBack: () => void;
  onResend: () => Promise<void>;
  onClearError: () => void;
}

export const QuickPurchaseOtpStep: FC<QuickPurchaseOtpStepProps> = ({
  otpForm,
  email,
  status,
  isLoading,
  maxRetriesExceeded,
  resendCooldownSeconds,
  onSubmit,
  onBack,
  onResend,
  onClearError,
}) => {
  const { t } = useTranslation(['quick-purchase']);
  const { formState } = otpForm;
  const hasValidationError = formState.isSubmitted && !!formState.errors.code;
  const hasBackendError = status === 'error' && !hasValidationError;

  return (
    <StyledStepContainer gap={6}>
      <StyledEmailIconWrapper>
        <DesignIcon iconName="Email" useMuiSvgIcon={false} />
      </StyledEmailIconWrapper>

      <StyledStepTitle>{t('modal.otp_step.title')}</StyledStepTitle>

      <Stack gap={0.5}>
        <Typography
          variant="body1"
          fontWeight={designTokens.typography.fontWeights.semiBold}
        >
          {t('modal.otp_step.description_email', { email })}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('modal.otp_step.description_hint')}
        </Typography>
      </Stack>

      <Stack gap={1}>
        <Controller
          name="code"
          control={otpForm.control}
          rules={{
            required: true,
            minLength: OTP_CODE_LENGTH,
            maxLength: OTP_CODE_LENGTH,
            pattern: /^\d+$/,
          }}
          render={({ field }) => (
            <StyledPillTextField
              {...field}
              onChange={(e) => {
                field.onChange(e);
                onClearError();
              }}
              placeholder={t('modal.otp_step.code_placeholder')}
              fullWidth
              inputProps={{ maxLength: OTP_CODE_LENGTH }}
              error={hasValidationError || hasBackendError}
            />
          )}
        />

        {hasValidationError && (
          <StyledErrorText>
            {t('modal.otp_step.error_code_length')}
          </StyledErrorText>
        )}
        {hasBackendError && !maxRetriesExceeded && (
          <StyledErrorText>{t('modal.otp_step.error_invalid')}</StyledErrorText>
        )}
        {maxRetriesExceeded && (
          <StyledErrorText>
            {t('modal.otp_step.error_max_retries')}
          </StyledErrorText>
        )}
      </Stack>

      <StyledButtonRow>
        <StyledPillButton
          variant="outlined"
          disabled={isLoading}
          onClick={onBack}
        >
          {t('modal.otp_step.back_button')}
        </StyledPillButton>
        <StyledPillButton
          variant="primary"
          disabled={isLoading || maxRetriesExceeded}
          onClick={otpForm.handleSubmit(onSubmit)}
        >
          {isLoading ? (
            <CircularProgress size={20} />
          ) : (
            t('modal.otp_step.verify_button')
          )}
        </StyledPillButton>
      </StyledButtonRow>

      <StyledOtpFooter>
        <StyledFooterText>{t('modal.otp_step.code_expires')}</StyledFooterText>
        <StyledFooterText>
          {resendCooldownSeconds > 0 ? (
            t('modal.otp_step.resend_cooldown', {
              seconds: resendCooldownSeconds,
            })
          ) : (
            <>
              {t('modal.otp_step.resend_hint_prefix')}
              <StyledResendLink
                onClick={onResend}
                disabled={maxRetriesExceeded}
              >
                {t('modal.otp_step.resend_link')}
              </StyledResendLink>
            </>
          )}
        </StyledFooterText>
      </StyledOtpFooter>
    </StyledStepContainer>
  );
};
